import { Fragment, useEffect, useId, useRef } from 'react'

import classNames from 'classnames'

import { BasicButton } from '@/Atomic/Atoms/BasicButton'
import { Icon } from '@/Atomic/Atoms/Icon'
import { MenuItem } from '@/Atomic/Molecules/MenuItem'
import { Tooltip } from '@/Atomic/Molecules/Tooltip'
import { useIconMenu } from '@/hooks/useIconMenu'
import { useSmartPosition } from '@/hooks/useSmartPosition'

import './SimpleSelect.scss'

export interface SimpleSelectOption {
  label: string
  value: string | boolean
}

interface SimpleSelectProps<TOption extends SimpleSelectOption> {
  name: string
  options: TOption[]
  value?: TOption
  onChange: (option: TOption) => void
  onClose?: () => void
  className?: string
  placeholder?: string
  isDisabled?: boolean
  openOnMount?: boolean
  group?: string
}

export const SimpleSelect = <TOption extends SimpleSelectOption>({
  name,
  options,
  value,
  onChange,
  onClose,
  className,
  placeholder = 'Select',
  isDisabled = false,
  openOnMount = false,
  group,
}: SimpleSelectProps<TOption>) => {
  const tooltipId = useId()
  const optionTooltipIdPrefix = `simple-select-option-${tooltipId.replace(/:/g, '')}`
  const { buttonRef, menuIsOpen, openMenu, setMenuIsOpen, toggleMenu } =
    useIconMenu(group ? { group } : undefined)
  const { panelRef, position, hasMeasured } = useSmartPosition({
    isOpen: menuIsOpen,
    triggerRef: buttonRef,
    preferHorizontal: 'right',
    preferVertical: 'bottom',
    listenToScroll: true,
  })
  const hasOpenedRef = useRef(false)

  const selectedValue = value?.value
  const triggerLabel = value?.label || placeholder

  useEffect(() => {
    if (openOnMount && !isDisabled) {
      openMenu()
    }
  }, [isDisabled, openMenu, openOnMount])

  useEffect(() => {
    if (menuIsOpen) {
      hasOpenedRef.current = true
      return
    }

    if (hasOpenedRef.current) {
      onClose?.()
    }
  }, [menuIsOpen, onClose])

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (isDisabled) return
    toggleMenu()
  }

  return (
    <div
      ref={buttonRef}
      className={classNames('m-simple-select', className, {
        'm-simple-select--open': menuIsOpen,
        'm-simple-select--disabled': isDisabled,
      })}
    >
      <BasicButton
        basicVariant="outlined"
        size="sm"
        className="m-simple-select__trigger"
        disabled={isDisabled}
        onClick={handleToggle}
      >
        <span className="m-simple-select__trigger-label">{triggerLabel}</span>
        <Icon icon="chevron-down" size="md" />
      </BasicButton>

      {menuIsOpen ? (
        <div
          ref={panelRef}
          className={classNames(
            'm-simple-select__menu',
            `m-simple-select__menu--${position}`,
          )}
          style={{
            visibility: hasMeasured ? 'visible' : 'hidden',
          }}
        >
          {options.map((option, index) => {
            const isSelected = option.value === selectedValue
            const optionTooltipId = `${optionTooltipIdPrefix}-${index}`

            return (
              <Fragment key={`${name}-${String(option.value)}`}>
                <MenuItem
                  id={optionTooltipId}
                  className={classNames('m-simple-select__option', {
                    'm-simple-select__option--selected': isSelected,
                  })}
                  label={option.label}
                  plainLabel
                  rightIcon={isSelected ? 'check' : undefined}
                  onClick={(e) => {
                    e.stopPropagation()
                    onChange(option)
                    setMenuIsOpen(false)
                  }}
                />
                <Tooltip
                  anchorSelect={`#${optionTooltipId}`}
                  content={option.label}
                />
              </Fragment>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
